import { useEffect, useRef, useState } from "react"

// Compact, Vertice-style feature browser: a left rail of feature tabs and a
// right panel that shows the active feature's title, description, and product
// visual. Auto-advances while in view (with a progress bar per tab), pauses on
// hover, and lets visitors click any tab to jump. Reduced-motion disables the
// auto-advance + transitions.

export interface Feature {
  id: string
  label: string
  icon: JSX.Element
  title: string
  description: string
  aspectRatio: string
  visual: JSX.Element
  /** How long this feature's tab stays active (ms) — matched to its visual's
      loop length so the demo isn't cut short. Falls back to AUTO_MS. */
  durationMs?: number
}

const AUTO_MS = 6500
// Advance this many ms BEFORE the visual's loop point, so a tab never sits long
// enough to show the demo snapping back to its start before switching.
const ADVANCE_LEAD_MS = 700
const tabMs = (f?: Feature) => Math.max(2500, (f?.durationMs ?? AUTO_MS) - ADVANCE_LEAD_MS)

export function FeatureTabs({ features }: { features: Feature[] }) {
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const [inView, setInView] = useState(false)
  const reduced = useRef(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    reduced.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const root = rootRef.current
    if (!root) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry) setInView(entry.isIntersecting)
      },
      { threshold: 0.25 },
    )
    io.observe(root)
    return () => io.disconnect()
  }, [])

  // Auto-advance: one timeout per active tab; resets on click/pause/visibility.
  useEffect(() => {
    if (paused || !inView || reduced.current) return
    const t = setTimeout(() => setActive((a) => (a + 1) % features.length), tabMs(features[active]))
    return () => clearTimeout(t)
  }, [active, paused, inView, features.length])

  const animate = inView && !paused && !reduced.current
  const current = features[active]
  if (!current) return null

  return (
    <div
      ref={rootRef}
      className="grid md:grid-cols-[minmax(0,360px)_1fr] gap-6 md:gap-12 items-center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Left rail — every tab shows its name + subtitle (so scanners see what
          each feature is). When active, the name shrinks to a tag, the subtitle
          becomes the heading, and the full description reveals below. */}
      <div role="tablist" aria-label="Features" className="flex flex-col gap-1.5">
        {features.map((f, i) => {
          const isActive = i === active
          return (
            <button
              key={f.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(i)}
              className={`lp-ft-tab ${isActive ? "active" : ""}`}
            >
              <span className="lp-ft-tab-icon">{f.icon}</span>
              <span className="lp-ft-tab-body">
                <span className="lp-ft-tab-label">{f.label}</span>
                <span className="lp-ft-tab-sub">{f.title}</span>
                {/* Visible for the active tab; kept in the DOM as screen-reader-only
                    for the rest, so prerendered HTML exposes every feature's copy to
                    crawlers / AI (otherwise only the active tab's description ships). */}
                <span className={isActive ? "lp-ft-tab-desc" : "sr-only"}>{f.description}</span>
              </span>
              {isActive && (
                <span
                  key={active}
                  className="lp-ft-progress"
                  data-animate={animate ? "true" : "false"}
                  style={{ animationDuration: `${tabMs(f)}ms` }}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Panel — on mobile it sits on top (always visible, stable during auto-advance);
          on md+ it's the right column. The frame stays mounted; only the graphic swaps + fades */}
      <div className="lp-ft-panel order-first md:order-none">
        <div
          key={active}
          className="lp-feat-visual"
          style={{ ["--feat-aspect" as string]: current.aspectRatio }}
        >
          {current.visual}
        </div>
      </div>
    </div>
  )
}
